import { useEffect, useMemo, useState } from 'react';
import {
  ACGME_KINDS,
  acgmeLabel,
  api,
  levelLabel,
  personName,
  type AcademicBlock,
  type AcgmeTotals,
  type BlockAssignmentRow,
  type ResidencySchedule,
  capabilityLabel,
  type MyProgram,
  type ProgramRole,
  type ResidentYearRow,
  type RoleSpec,
} from '../lib/api';
import { navigate } from '../lib/router';

/**
 * The residency, as two screens (D43).
 *
 * Both are read-only, deliberately. A program's block schedule is the
 * program's own document: it arrives by import, whole and checked, and is
 * corrected by re-importing a corrected file. Letting somebody drag a
 * resident between blocks here would create a second place the truth lives,
 * and the first thing that happens then is that the two disagree.
 *
 * The one door in is ResidencyImport (D48), which writes nothing without
 * showing what it would do first.
 *
 * What these screens are for is proving the whole path works — that 1,950
 * imported resident-blocks come back out as a roster somebody recognises and
 * a board somebody can read. They are also the first place the rotation
 * vocabulary earns its keep: every count below is counted once per rotation
 * rather than once per spelling.
 */

// ---------------------------------------------------------------------------
// Picking a program: client, schedule, year
// ---------------------------------------------------------------------------
//
// Exported for the import screen, which is a fourth screen on the same
// program and has to pick it the same way. One resolver rather than two
// means a link carrying a scheduleId lands on the same program whichever
// tab it names.

export interface Program {
  clientId: string;
  schedules: ResidencySchedule[];
  schedule: ResidencySchedule;
  blocks: AcademicBlock[];
  years: string[];
  year: string;
}

type Loaded = { state: 'loading' } | { state: 'error'; message: string } | { state: 'empty' } | { state: 'ready'; program: Program };

/**
 * Resolve which program this page is about.
 *
 * `clients` comes from the login: a list for a client's own schedulers, null
 * for an owner, who may reach every one. Either way the id in the URL wins,
 * so a link to a particular program is a link somebody can send.
 */
export function useProgram(clients: string[] | null, scheduleId: string | null, year: string | null): Loaded {
  const [loaded, setLoaded] = useState<Loaded>({ state: 'loading' });

  useEffect(() => {
    let live = true;
    const fail = (e: Error) => live && setLoaded({ state: 'error', message: e.message });

    const clientIds = async (): Promise<string[]> => {
      if (clients && clients.length) return clients;
      // An owner: no client of their own, so ask which ones exist.
      const rows = await api.orgClients();
      return rows.map((r) => r.id);
    };

    (async () => {
      const ids = await clientIds();
      // The first client that actually runs a residency. An owner's list is
      // every customer, most of which do not, so this is a search rather
      // than a guess at ids[0].
      for (const clientId of ids) {
        const schedules = await api.residencySchedules(clientId);
        if (!schedules.length) continue;
        const schedule = schedules.find((s) => s.id === scheduleId) ?? schedules[0];
        const blocks = await api.residencyBlocks(clientId, schedule.id);
        const years = [...new Set(blocks.map((b) => b.academicYear))].sort().reverse();
        if (!years.length) {
          if (live) setLoaded({ state: 'ready', program: { clientId, schedules, schedule, blocks, years, year: '' } });
          return;
        }
        const chosen = (year && years.includes(year) ? year : null) ?? years[0];
        if (live) {
          setLoaded({
            state: 'ready',
            program: { clientId, schedules, schedule, blocks: blocks.filter((b) => b.academicYear === chosen), years, year: chosen },
          });
        }
        return;
      }
      if (live) setLoaded({ state: 'empty' });
    })().catch(fail);

    return () => {
      live = false;
    };
  }, [clients?.join(','), scheduleId, year]);

  return loaded;
}

export function ProgramHeader({ program, path }: { program: Program; path: string }) {
  const { schedule, schedules, years, year } = program;
  const go = (next: { schedule?: string; year?: string }) => {
    const q = new URLSearchParams();
    q.set('scheduleId', next.schedule ?? schedule.id);
    q.set('year', next.year ?? year);
    navigate(`${path}?${q}`);
  };
  return (
    <div className="gridtools">
      <div className="crumbs">
        <strong>{schedule.name}</strong> <span className="muted">· {schedule.orgUnit.name}</span>
      </div>
      {schedules.length > 1 && (
        <label className="field">
          Schedule{' '}
          <select value={schedule.id} onChange={(e) => go({ schedule: e.target.value })}>
            {schedules.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </label>
      )}
      {years.length > 0 && (
        <label className="field">
          Academic year{' '}
          <select value={year} onChange={(e) => go({ year: e.target.value })}>
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}

/** The three states before a program is on screen, said the same way on both pages. */
export function Gate({ loaded }: { loaded: Loaded }) {
  if (loaded.state === 'loading') return <p className="muted">Loading…</p>;
  if (loaded.state === 'error') return <p className="error">Couldn't load the residency ({loaded.message}).</p>;
  return (
    <p className="muted">
      No residency schedule here yet. One is created on the{' '}
      <button className="linklike" type="button" onClick={() => navigate('/admin/org')}>Organization</button> screen, then the
      program's year is imported into it.
    </p>
  );
}

/** A year with no blocks in it: imported schedule, nothing uploaded yet. */
function NothingImported() {
  return (
    <p className="muted">
      This schedule has no blocks yet. A program's year is uploaded whole — twenty-five blocks, every resident — and checked
      before anything is written.
    </p>
  );
}

// ---------------------------------------------------------------------------
// Screen one: the roster
// ---------------------------------------------------------------------------

/**
 * Who is in the program, at what level, and what their year adds up to.
 *
 * The per-kind columns are the reason the rotation vocabulary exists. Before
 * it, "Pulm", "Pulmonology" and "Pulmonlogy" were three rotations and every
 * one of these numbers was wrong.
 */
export function ResidencyRoster({ clients, scheduleId, year, physicianId }: { clients: string[] | null; scheduleId: string | null; year: string | null; physicianId: string | null }) {
  const loaded = useProgram(clients, scheduleId, year);
  if (loaded.state !== 'ready') return <Gate loaded={loaded} />;
  const { program } = loaded;
  return (
    <section className="residency">
      <ProgramHeader program={program} path="/admin/residency" />
      {program.year ? <RosterBody program={program} physicianId={physicianId} /> : <NothingImported />}
    </section>
  );
}

function RosterBody({ program, physicianId }: { program: Program; physicianId: string | null }) {
  const { clientId, schedule, year } = program;
  const [raw, setRaw] = useState<ResidentYearRow[] | null>(null);
  const [totals, setTotals] = useState<AcgmeTotals[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setRaw(null);
    setTotals(null);
    Promise.all([api.residencyRoster(clientId, schedule.id, year), api.residencyAcgme(clientId, schedule.id, year)])
      .then(([r, t]) => {
        if (!live) return;
        setRaw(r);
        setTotals(t);
      })
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [clientId, schedule.id, year]);

  if (error) return <p className="error">Couldn't load the roster ({error}).</p>;
  if (!raw || !totals) return <p className="muted">Loading…</p>;
  if (!raw.length) return <NothingImported />;

  // The server orders by surname, which is right for names and wrong for the
  // numbered keys an anonymised roster uses — "Resident 30" sorts before
  // "Resident 4". Ordered here so both readings work.
  const roster = [...raw].sort((a, b) => a.pgy - b.pgy || collate(personName(a.physician), personName(b.physician)));
  const byPhysician = new Map(totals.map((t) => [t.physicianId, t]));
  // Only the kinds this program actually uses: a column of zeroes is noise.
  const kinds = ACGME_KINDS.filter((k) => totals.some((t) => t.kinds[k]));
  const cohorts = tally(roster.map((r) => levelLabel(r)));
  const programKinds = kinds.map((k) => [k, totals.reduce((n, t) => n + (t.kinds[k] ?? 0), 0)] as const);
  const blockTotal = totals.reduce((n, t) => n + t.blocks, 0);

  return (
    <>
      <ul className="orgstats">
        <li><strong>{roster.length}</strong> residents</li>
        {[...cohorts].map(([label, n]) => (
          <li key={label}><strong>{n}</strong> {label}</li>
        ))}
        <li><strong>{blockTotal.toLocaleString()}</strong> resident-blocks</li>
      </ul>

      <div className="card">
        <h2>The year, by what it counts as</h2>
        <ul className="orgstats">
          {programKinds.map(([kind, n]) => (
            <li key={kind}>
              <strong>{n}</strong> {acgmeLabel(kind)} <span className="muted">{pct(n, blockTotal)}</span>
            </li>
          ))}
        </ul>
        <p className="small muted">
          One block is two weeks, so half an ACGME month. Counted once per rotation, not once per spelling — the schedule
          writes some of these three different ways.
        </p>
      </div>

      <div className="card">
        <h2>Residents</h2>
        <div className="gridscroll">
          <table className="grid residents">
            <thead>
              <tr>
                <th>Resident</th>
                <th>Level</th>
                <th className="num">Blocks</th>
                <th className="num">Clinical</th>
                {kinds.map((k) => (
                  <th key={k} className="num">{acgmeLabel(k)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {roster.map((r) => {
                const t = byPhysician.get(r.physicianId);
                const open = physicianId === r.physicianId;
                return (
                  <tr key={r.id} className={open ? 'open-row' : undefined}>
                    <td>
                      <button
                        className="linklike"
                        type="button"
                        onClick={() => navigate(`/admin/residency?scheduleId=${schedule.id}&year=${year}${open ? '' : `&physicianId=${r.physicianId}`}`)}
                      >
                        {personName(r.physician)}
                      </button>
                      {personName(r.physician) !== r.physician.abbreviation && (
                        <span className="muted small"> · {r.physician.abbreviation}</span>
                      )}
                      {!r.active && <span className="muted small"> · left the program</span>}
                    </td>
                    <td>{levelLabel(r)}</td>
                    <td className="num">{t?.blocks ?? 0}</td>
                    <td className="num">{t?.clinicalBlocks ?? 0}</td>
                    {kinds.map((k) => (
                      <td key={k} className="num">{t?.kinds[k] || <span className="muted">·</span>}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {physicianId && <ResidentYear program={program} physicianId={physicianId} roster={roster} />}
    </>
  );
}

/** One resident's twenty-five blocks, in order. */
function ResidentYear({ program, physicianId, roster }: { program: Program; physicianId: string; roster: ResidentYearRow[] }) {
  const { clientId, schedule, year } = program;
  const [rows, setRows] = useState<BlockAssignmentRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const who = roster.find((r) => r.physicianId === physicianId);

  useEffect(() => {
    let live = true;
    setRows(null);
    api
      .residencyAssignments(clientId, schedule.id, { year, physicianId })
      .then((r) => live && setRows(r))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [clientId, schedule.id, year, physicianId]);

  return (
    <div className="card">
      <h2>
        {who ? `${personName(who.physician)} — ${levelLabel(who)}` : 'This resident'}
        <button className="linklike" type="button" onClick={() => navigate(`/admin/residency?scheduleId=${schedule.id}&year=${year}`)}>close</button>
      </h2>
      {error && <p className="error">Couldn't load their year ({error}).</p>}
      {!rows ? (
        <p className="muted">Loading…</p>
      ) : (
        <ul className="orgschedules">
          {rows.map((a) => (
            <li key={a.id}>
              <span className="muted" style={{ minWidth: 68 }}>Block {a.block.number}</span>
              <span className="orgname">{a.rotation.name}</span>
              <span className="chip">{acgmeLabel(a.rotation.acgmeKind)}</span>
              {a.sourceLabel && a.sourceLabel !== a.rotation.name && (
                <span className="muted small">written “{a.sourceLabel}”</span>
              )}
              <span className="muted small" style={{ marginLeft: 'auto' }}>{a.block.startDate} → {a.block.endDate}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Screen two: the block board
// ---------------------------------------------------------------------------

/**
 * Where everybody is. Two ways of looking at the same 1,950 rows: one block
 * at a time (who is on what), or the whole year at once (one resident per
 * row, twenty-five columns).
 *
 * The whole-year grid is loaded only when asked for. One block is 78 rows;
 * the year is 1,950, and a screen that pulls all of it to show one block
 * would be slow for no reason.
 */
export function ResidencyBlocks({ clients, scheduleId, year, block }: { clients: string[] | null; scheduleId: string | null; year: string | null; block: string | null }) {
  const loaded = useProgram(clients, scheduleId, year);
  if (loaded.state !== 'ready') return <Gate loaded={loaded} />;
  const { program } = loaded;
  return (
    <section className="residency">
      <ProgramHeader program={program} path="/admin/residency/blocks" />
      {program.year ? <BoardBody program={program} block={block} /> : <NothingImported />}
    </section>
  );
}

function BoardBody({ program, block }: { program: Program; block: string | null }) {
  const { clientId, schedule, year, blocks } = program;
  const wholeYear = block === 'all';
  const number = wholeYear ? null : Number(block) || blocks[0]?.number || 1;
  const [rows, setRows] = useState<BlockAssignmentRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setRows(null);
    setError(null);
    api
      .residencyAssignments(clientId, schedule.id, wholeYear ? { year } : { year, block: number! })
      .then((r) => live && setRows(r))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [clientId, schedule.id, year, number, wholeYear]);

  const to = (b: number | 'all') => `/admin/residency/blocks?scheduleId=${schedule.id}&year=${year}&block=${b}`;

  if (!blocks.length) return <NothingImported />;

  return (
    <>
      <div className="card">
        <h2>
          Blocks
          <button className="linklike" type="button" onClick={() => navigate(to(wholeYear ? blocks[0].number : 'all'))}>
            {wholeYear ? 'one block at a time' : 'the whole year'}
          </button>
        </h2>
        <ul className="blockstrip">
          {blocks.map((b) => (
            <li key={b.id}>
              <button
                type="button"
                className={!wholeYear && b.number === number ? 'current' : undefined}
                onClick={() => navigate(to(b.number))}
                title={`${b.startDate} → ${b.endDate}`}
              >
                <strong>{b.number}</strong>
                <span>{shortDate(b.startDate)}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {error && <p className="error">Couldn't load the board ({error}).</p>}
      {!rows ? <p className="muted">Loading…</p> : wholeYear ? <YearGrid blocks={blocks} rows={rows} /> : <OneBlock blocks={blocks} number={number!} rows={rows} />}
    </>
  );
}

/** One block: who is on what, grouped by rotation. */
function OneBlock({ blocks, number, rows }: { blocks: AcademicBlock[]; number: number; rows: BlockAssignmentRow[] }) {
  const here = blocks.find((b) => b.number === number);
  const byRotation = useMemo(() => {
    const map = new Map<string, { kind: string; people: BlockAssignmentRow[] }>();
    for (const row of rows) {
      const entry = map.get(row.rotation.name) ?? { kind: row.rotation.acgmeKind, people: [] };
      entry.people.push(row);
      map.set(row.rotation.name, entry);
    }
    return [...map.entries()].sort((a, b) => b[1].people.length - a[1].people.length || a[0].localeCompare(b[0]));
  }, [rows]);

  return (
    <div className="card">
      <h2>
        Block {number}
        {here && <span className="muted"> · {here.startDate} → {here.endDate}</span>}
        <span className="muted">{rows.length} residents</span>
      </h2>
      {!rows.length ? (
        <p className="muted">Nobody is scheduled in this block.</p>
      ) : (
        <ul className="orgchildren">
          {byRotation.map(([name, { kind, people }]) => (
            <li key={name}>
              <span className="orgname">{name}</span>
              <span className="chip">{acgmeLabel(kind)}</span>
              <span className="muted small">{people.length}</span>
              <span className="people" style={{ marginLeft: 'auto', textAlign: 'right', maxWidth: '70%' }}>
                {people.map((p) => personName(p.physician)).sort(collate).join(', ')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The whole year: one resident per row, one column per block. */
function YearGrid({ blocks, rows }: { blocks: AcademicBlock[]; rows: BlockAssignmentRow[] }) {
  const grid = useMemo(() => {
    const people = new Map<string, { id: string; who: string; cells: Map<number, BlockAssignmentRow> }>();
    for (const row of rows) {
      const entry = people.get(row.physicianId) ?? { id: row.physicianId, who: personName(row.physician), cells: new Map() };
      entry.cells.set(row.block.number, row);
      people.set(row.physicianId, entry);
    }
    return [...people.values()].sort((a, b) => collate(a.who, b.who));
  }, [rows]);

  return (
    <div className="card">
      <h2>
        The whole year
        <span className="muted">{grid.length} residents × {blocks.length} blocks</span>
      </h2>
      <div className="gridscroll">
        <table className="grid blockgrid">
          <thead>
            <tr>
              <th>Resident</th>
              {blocks.map((b) => (
                <th key={b.id} title={`${b.startDate} → ${b.endDate}`}>{b.number}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.map((person) => (
              <tr key={person.id}>
                <td className="cellname">{person.who}</td>
                {blocks.map((b) => {
                  const cell = person.cells.get(b.number);
                  return (
                    <td key={b.id} className={cell ? `kind-${cell.rotation.acgmeKind}` : undefined} title={cell ? `${cell.rotation.name} — ${acgmeLabel(cell.rotation.acgmeKind)}` : undefined}>
                      {cell ? cell.rotation.name : <span className="muted">·</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="legend">
        {ACGME_KINDS.filter((k) => rows.some((r) => r.rotation.acgmeKind === k)).map((k) => (
          <li key={k}><span className={`swatch kind-${k}`} /> {acgmeLabel(k)}</li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------

function tally(labels: string[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const l of labels) out.set(l, (out.get(l) ?? 0) + 1);
  return new Map([...out].sort((a, b) => a[0].localeCompare(b[0])));
}

const pct = (n: number, total: number) => (total ? `${((n / total) * 100).toFixed(1)}%` : '');

const shortDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

/** "Resident 9" before "Resident 10": anonymised keys are numbered, and alphabetical order reads as broken. */
function collate(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

// ---------------------------------------------------------------------------
// Screen three: who runs the program
// ---------------------------------------------------------------------------

/**
 * The people who run a residency, and what each of them may do (D45).
 *
 * This is the screen that makes the coordinator/director separation somebody's
 * to maintain rather than CallCovered's. A director adds their own chief
 * residents and associate directors here; nobody has to be called.
 *
 * Read-only for everyone else — the API refuses a grant from anybody but a
 * director, and this page says so rather than offering a button that fails.
 */
export function ResidencyRoles({
  clients,
  scheduleId,
  year,
  editedBy,
  myRoles,
  owner,
}: {
  clients: string[] | null;
  scheduleId: string | null;
  year: string | null;
  editedBy: string | null;
  /** This login's own roles, so the screen offers only what it may actually give. */
  myRoles: MyProgram[];
  owner: boolean;
}) {
  const loaded = useProgram(clients, scheduleId, year);
  if (loaded.state !== 'ready') return <Gate loaded={loaded} />;
  return (
    <section className="residency">
      <ProgramHeader program={loaded.program} path="/admin/residency/roles" />
      <RolesBody program={loaded.program} editedBy={editedBy} myRoles={myRoles} owner={owner} />
    </section>
  );
}

function RolesBody({
  program,
  editedBy,
  myRoles,
  owner,
}: {
  program: Program;
  editedBy: string | null;
  myRoles: MyProgram[];
  owner: boolean;
}) {
  const { clientId, schedule } = program;
  const [roles, setRoles] = useState<ProgramRole[] | null>(null);
  const [catalog, setCatalog] = useState<RoleSpec[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setError(null);
    Promise.all([api.programRoles(clientId, schedule.id), api.programRoleCatalog()])
      .then(([r, c]) => { setRoles(r); setCatalog(c); })
      .catch((e: Error) => setError(e.message));
  };
  useEffect(load, [clientId, schedule.id]);

  const grant = (email: string, role: string) => {
    setBusy(true);
    setError(null);
    api
      .grantProgramRole(clientId, schedule.id, { email, role, grantedBy: editedBy })
      .then(() => load())
      .catch((e: Error) => setError(e.message))
      .finally(() => setBusy(false));
  };
  const revoke = (id: string, email: string) => {
    if (!window.confirm(`End ${email}'s role on this program? The record of it is kept.`)) return;
    setBusy(true);
    setError(null);
    api
      .revokeProgramRole(clientId, schedule.id, id, editedBy)
      .then(() => load())
      .catch((e: Error) => setError(e.message))
      .finally(() => setBusy(false));
  };

  if (error && !roles) return <p className="error">Couldn't load who runs this program ({error}).</p>;
  if (!roles || !catalog) return <p className="muted">Loading…</p>;

  // What this login may hand out. A director gives any of them; a chief
  // resident or coordinator gives the evaluator badge and nothing else. The
  // API decides this for real — offering less here just means a refusal is
  // never how somebody discovers the rule.
  const mine = myRoles.find((r) => r.scheduleId === program.schedule.id);
  const grantable = owner || mine?.capabilities.includes('program.manage-roles')
    ? catalog.map((c) => c.key)
    : mine?.capabilities.includes('program.manage-evaluators')
      ? ['evaluator']
      : [];

  const current = roles.filter((r) => r.active);
  const ended = roles.filter((r) => !r.active);
  const byRole = catalog.map((spec) => ({ spec, people: current.filter((r) => r.role === spec.key) }));

  return (
    <>
      {error && <p className="error">{error}</p>}

      <div className="card">
        <h2>Who runs this program</h2>
        {!current.length ? (
          <p className="muted">
            Nobody yet. A program's first director is appointed by CallCovered; after that the director appoints everybody else
            here.
          </p>
        ) : (
          <ul className="orgchildren">
            {byRole.map(({ spec, people }) => (
              <li key={spec.key}>
                <span className="orgname">{spec.label}</span>
                <span className="muted small">{people.length || <>nobody</>}</span>
                <span className="people" style={{ marginLeft: 'auto', textAlign: 'right' }}>
                  {people.map((p) => p.email).join(', ') || '—'}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="small muted">
          Any number of people can hold any role — a program usually has several chief residents and more than one associate
          director. One role each; giving somebody a different one replaces the one they had.
        </p>
      </div>

      <div className="card">
        <h2>What each role may do</h2>
        <div className="gridscroll">
          <table className="grid residents">
            <thead>
              <tr>
                <th>Role</th>
                {CAPABILITY_ORDER.map((c) => (
                  <th key={c}>{capabilityLabel(c)}</th>
                ))}
                <th>People</th>
              </tr>
            </thead>
            <tbody>
              {catalog.map((spec) => (
                <tr key={spec.key}>
                  <td><strong>{spec.label}</strong><div className="muted small">{spec.about}</div></td>
                  {CAPABILITY_ORDER.map((c) => (
                    <td key={c} style={{ textAlign: 'center' }}>
                      {spec.capabilities.includes(c) ? '✓' : <span className="muted">·</span>}
                    </td>
                  ))}
                  <td style={{ textAlign: 'right' }}>{current.filter((r) => r.role === spec.key).length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="small muted">
          Nobody reads or records their own assessment, whatever role they hold — which matters most for a chief resident, who is
          also a resident being assessed.
        </p>
      </div>

      <AddRole catalog={catalog} grantable={grantable} busy={busy} onGrant={grant} />

      {current.length > 0 && (
        <div className="card">
          <h2>Everyone, in detail</h2>
          <div className="gridscroll">
            <table className="grid residents">
              <thead>
                <tr><th>Who</th><th>Role</th><th>Granted by</th><th>When</th><th /></tr>
              </thead>
              <tbody>
                {current.map((r) => (
                  <tr key={r.id}>
                    <td>{r.email}</td>
                    <td>{r.label ?? r.role}</td>
                    <td className="muted">{r.grantedBy}</td>
                    <td className="muted">{r.grantedAt.slice(0, 10)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="linklike" type="button" disabled={busy} onClick={() => revoke(r.id, r.email)}>end</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {ended.length > 0 && (
        <details className="history">
          <summary>{ended.length} role{ended.length === 1 ? '' : 's'} that have ended</summary>
          <ul className="orgschedules">
            {ended.map((r) => (
              <li key={r.id}>
                <span className="orgname">{r.email}</span>
                <span className="chip">{r.label ?? r.role}</span>
                <span className="muted small" style={{ marginLeft: 'auto' }}>{r.notes ?? `granted ${r.grantedAt.slice(0, 10)}`}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}

function AddRole({
  catalog,
  grantable,
  busy,
  onGrant,
}: {
  catalog: RoleSpec[];
  /** The roles THIS login may hand out. A chief or coordinator has one. */
  grantable: string[];
  busy: boolean;
  onGrant: (email: string, role: string) => void;
}) {
  const offered = catalog.filter((c) => grantable.includes(c.key));
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(offered[0]?.key ?? '');
  const chosen = offered.find((c) => c.key === role) ?? offered[0];
  if (!offered.length) return null;
  return (
    <div className="card">
      <h2>Give somebody a role</h2>
      <form
        className="addclient"
        onSubmit={(e) => {
          e.preventDefault();
          if (!email.trim()) return;
          onGrant(email.trim(), role);
          setEmail('');
        }}
      >
        <input
          type="email"
          value={email}
          placeholder="their sign-in address"
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <select value={chosen?.key ?? ''} onChange={(e) => setRole(e.target.value)} disabled={offered.length === 1}>
          {offered.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>
        <button type="submit" disabled={busy || !email.trim()}>Give the role</button>
      </form>
      {chosen && (
        <p className="small muted">
          {chosen.about} They will be able to: {chosen.capabilities.map(capabilityLabel).join(', ').toLowerCase()}.
        </p>
      )}
      <p className="small muted">
        {offered.length === 1
          ? `You can appoint ${offered[0].label}s. Any other role is the program director's to give.`
          : 'A program director can give any of these.'}{' '}
        The address must be the one they sign in with, and it cannot be your own.
      </p>
    </div>
  );
}

/** The order the capability columns read best in — widest reach first. */
const CAPABILITY_ORDER = [
  'program.manage-roles',
  'program.manage-evaluators',
  'performance.record',
  'performance.read',
  'performance.read-own',
  'schedule.import',
  'schedule.read',
];
